import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '../../chat/api'
import { FeatherIcon } from '../../components/FeatherIcon'
import { LoadingBlock } from '../../components/Spinner'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useCrmRequest } from '../CrmRequest'
import { bankingMoney, systemHeading, toBankingSummary, type BankingSummary } from './bankingData'
import '../../library/library.css'
import '../ofPages.css'
import './banking.css'

interface Props {
  accountId: string
  onBack: () => void
  mobileNav?: ReactNode
}

/**
 * The last summary per account for this session: going back to Banking shows
 * it at once while a fresh one loads. Never shown under another account.
 */
const lastSummary = new Map<string, BankingSummary>()

/** Banking: the creator's payout status and destination (onlyfans.com/my/banking), read-only. */
export function BankingPage({ accountId, onBack, mobileNav }: Props) {
  const { t } = useTranslation('ofBanking')
  const request = useCrmRequest()
  const mobile = useIsNarrow()
  const [data, setData] = useState<BankingSummary | null>(() => lastSummary.get(accountId) ?? null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const generation = useRef(0)

  const load = useCallback(async () => {
    const mine = ++generation.current
    setLoading(true)
    setError('')
    try {
      const { status, data: body } = await request<Record<string, unknown>>(`/v1/banking/summary?account_id=${encodeURIComponent(accountId)}`, {
        method: 'GET',
        local: true,
      })
      if (mine !== generation.current) return
      if (status !== 200 || body?.ok === false) {
        setError(errorMessage(body, `HTTP ${status}`))
        return
      }
      const summary = toBankingSummary(body?.data)
      lastSummary.set(accountId, summary)
      setData(summary)
    } catch (e) {
      if (mine === generation.current) setError(e instanceof Error && e.message ? e.message : t('requestFailed'))
    } finally {
      if (mine === generation.current) setLoading(false)
    }
  }, [request, accountId, t])

  useEffect(() => {
    void load()
    return () => {
      generation.current += 1
    }
  }, [load])

  const head = (
    <header className="lib-main-head ofb-head">
      <button type="button" className="lib-icon-btn" aria-label={t('back')} title={t('back')} onClick={onBack}>
        <FeatherIcon name="arrow-left" size={19} />
      </button>
      <h1 className="lib-main-title ofb-title">{t('title')}</h1>
      <span className="lib-spacer" />
      <button type="button" className={`lib-icon-btn ${loading && data ? 'om-busy' : ''}`} aria-label={t('reload')} title={t('reload')} onClick={() => void load()} disabled={loading}>
        <FeatherIcon name="refresh-cw" size={17} />
      </button>
    </header>
  )

  let body: ReactNode
  if (data) {
    body = (
      <>
        {error && <ErrorCard message={t('loadFailed', { error })} retry={t('retry')} onRetry={() => void load()} />}
        <StatusCard d={data} />
        <SelectedCard d={data} />
        <OptionsCard d={data} />
        <p className="ofb-footnote">
          <FeatherIcon name="info" size={16} />
          <span>{t('footnote')}</span>
        </p>
      </>
    )
  } else if (loading) {
    body = <LoadingBlock label={t('loading')} />
  } else {
    body = <ErrorCard message={t('loadFailed', { error: error || '—' })} retry={t('retry')} onRetry={() => void load()} />
  }

  return (
    <div className={`lib ofp ofb ${mobile ? 'lib--m ofb--m' : ''}`}>
      <main className="lib-main">
        {head}
        <div className="ofb-scroll">
          <div className="ofb-content">{body}</div>
        </div>
      </main>
      {mobile && mobileNav}
    </div>
  )
}

function ErrorCard({ message, retry, onRetry }: { message: string; retry: string; onRetry: () => void }) {
  return (
    <div className="ofb-card ofb-error" role="alert">
      <span>{message}</span>
      <button type="button" className="ofb-link-btn" onClick={onRetry}>
        {retry}
      </button>
    </div>
  )
}

function External({ href, label, primary = false }: { href: string; label: string; primary?: boolean }) {
  return (
    <a className={`ofb-action ${primary ? 'is-primary' : ''}`} href={href} target="_blank" rel="noopener noreferrer">
      <span>{label}</span>
      <FeatherIcon name="external-link" size={13} />
    </a>
  )
}

function Facts({ rows, mono = false }: { rows: [string, string][]; mono?: boolean }) {
  if (!rows.length) return null
  return (
    <dl className="ofb-facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd className={mono ? 'ofb-mono' : undefined}>{v}</dd>
        </div>
      ))}
    </dl>
  )
}

function StatusCard({ d }: { d: BankingSummary }) {
  const { t } = useTranslation('ofBanking')
  const { status, country } = d
  const system = d.payout?.system
  const heading = system?.title ? t('yourAccount', { system: systemHeading(system.title) }) : t('title')
  let banner: ReactNode = null
  if (!status) {
    if (d.errors.legal) banner = <div className="ofb-banner is-warn">{t('loadFailed', { error: d.errors.legal })}</div>
  } else if (status.approved) {
    banner = (
      <div className="ofb-banner is-ok">
        <FeatherIcon name="check-circle" size={18} />
        <strong>{t('approved')}</strong>
      </div>
    )
  } else {
    banner = (
      <div className="ofb-banner is-warn">
        <FeatherIcon name="alert-triangle" size={18} />
        <div>
          <strong>{t('ivStatus', { status: status.ivStatus || '—' })}</strong>
          {status.rejectReason && <p>{status.rejectReason}</p>}
          <div className="ofb-banner-actions">
            <External href={d.editUrl} label={t('completeOnOf')} />
          </div>
        </div>
      </div>
    )
  }
  const facts: [string, string][] = []
  if (country) facts.push([t('payoutCountry'), `${country.name}${country.code ? ` (${country.code})` : ''}`])
  if (status) facts.push([t('accountType'), t(status.isCompany ? 'company' : 'individual')])
  if (status?.w9Required) facts.push(['W-9', t(status.w9Exists ? 'submitted' : 'required')])
  return (
    <section className="ofb-card" aria-label={heading}>
      <div className="ofb-card-head">
        <span>{heading}</span>
      </div>
      {banner}
      <Facts rows={facts} />
    </section>
  )
}

function SelectedCard({ d }: { d: BankingSummary }) {
  const { t } = useTranslation('ofBanking')
  const head = (
    <div className="ofb-card-head">
      <span>{t('selectedSystem')}</span>
    </div>
  )
  if (!d.payout) {
    return (
      <section className="ofb-card">
        {head}
        <div className="ofb-banner is-warn">{t('loadFailed', { error: d.errors.bank || '—' })}</div>
      </section>
    )
  }
  const s = d.payout.system
  if (!s || !d.payout.filled) {
    return (
      <section className="ofb-card">
        {head}
        <div className="ofb-empty">{t('noSystem')}</div>
        <div className="ofb-actions">
          <External href={d.editUrl} label={t('setUpOnOf')} primary />
        </div>
      </section>
    )
  }
  return (
    <section className="ofb-card">
      {head}
      <div className="ofb-system">
        <div className="ofb-system-icon" aria-hidden="true">
          <FeatherIcon name="credit-card" size={22} />
        </div>
        <div>
          <div className="ofb-system-title">{s.title}</div>
          {s.subtitle && <div className="ofb-system-sub">{s.subtitle}</div>}
          <div className="ofb-meta">
            {s.minPayout !== null && <span>{t('minPayout', { min: bankingMoney(s.minPayout) })}</span>}
            {s.payoutTime && <span>{t('payoutTime', { time: s.payoutTime })}</span>}
          </div>
        </div>
      </div>
      <Facts rows={s.fields.map((f) => [f.label, f.filled ? f.value : '—'])} mono />
      {s.description && <p className="ofb-description">{s.description}</p>}
      <div className="ofb-actions">
        <External href={d.editUrl} label={t('editInfo')} primary />
      </div>
    </section>
  )
}

function OptionsCard({ d }: { d: BankingSummary }) {
  const { t } = useTranslation('ofBanking')
  if (!d.options) {
    if (!d.errors.options) return null
    return (
      <section className="ofb-card">
        <div className="ofb-card-head">
          <span>{t('otherOptions')}</span>
        </div>
        <div className="ofb-banner is-warn">{t('loadFailed', { error: d.errors.options })}</div>
      </section>
    )
  }
  if (!d.options.length) return null
  const canChange = d.status?.canChangePayoutType !== false
  return (
    <section className="ofb-card">
      <div className="ofb-card-head">
        <span>{t('otherOptions')}</span>
        {canChange && <External href={d.optionsUrl} label={t('changeOnOf')} />}
      </div>
      <ul className="ofb-options">
        {d.options.map((o) => (
          <li key={o.code || o.title} className={o.selected ? 'is-selected' : undefined}>
            <div>
              <div className="ofb-option-title">{o.title}</div>
              {o.subtitle && <div className="ofb-option-sub">{o.subtitle}</div>}
            </div>
            <div className="ofb-meta ofb-option-meta">
              {o.minPayout !== null && <span>{t('minPayout', { min: bankingMoney(o.minPayout) })}</span>}
              {o.payoutTime && <span>{o.payoutTime}</span>}
            </div>
            {o.selected ? <span className="ofb-badge">{t('selected')}</span> : <span />}
          </li>
        ))}
      </ul>
    </section>
  )
}
